import {
  useMemo,
  useState,
} from "react";

import {
  supabase,
} from "../../lib/supabaseClient";

import {
  usePrpSessionState,
} from "../../lib/prpUiContinuity";

// LOT0F5_PRP_NEARBY_TO_PIPELINE_LIVE_REFRESH
//
// The PRP's device position is requested only after an explicit human click.
// It remains ephemeral React state. No browser-storage API, CRM write,
// business write, or coordinate verification occurs in this panel.

type AnyRecord = Record<string, unknown>;

type NearbyCategoryKey =
  | "restaurants"
  | "spas"
  | "hotels"
  | "cafes"
  | "boutiques"
  | "activities"
  | "local_businesses";

type NearbyPlace = {
  provider_place_id: string;
  name: string;
  address: string;
  primary_type: string;
  primary_type_label: string;
  latitude: number | null;
  longitude: number | null;
  distance_m: number | null;
  source: "google_maps_places";
  verification_state: "temporary_unverified";
};

type NearbySaveState = {
  state: "saving" | "saved" | "duplicate" | "error";
  lead_id?: string;
  error?: string;
};

type ForfeoFitTier = "A" | "B" | "C";

const CATEGORY_OPTIONS: Array<{
  key: NearbyCategoryKey;
  label: string;
}> = [
  { key: "restaurants", label: "Restaurants" },
  { key: "spas", label: "Spas & bien-être" },
  { key: "hotels", label: "Hôtels" },
  { key: "cafes", label: "Cafés" },
  { key: "boutiques", label: "Boutiques" },
  { key: "activities", label: "Activités" },
  { key: "local_businesses", label: "Commerces locaux" },
];

const RADIUS_MIN_KM = 1;
const RADIUS_MAX_KM = 100;

const FORFEO_VERTICAL_PRIORITY: Record<string, number> = {
  spa: 100,
  massage_spa: 100,
  wellness_center: 100,
  hotel: 98,
  resort_hotel: 98,
  bed_and_breakfast: 96,
  restaurant: 95,
  tourist_attraction: 94,
  amusement_park: 92,
  museum: 90,
  aquarium: 90,
  zoo: 90,
  adventure_sports_center: 90,
  cafe: 86,
  coffee_shop: 86,
  bakery: 82,
  gift_shop: 80,
  jewelry_store: 76,
  clothing_store: 74,
  book_store: 72,
  store: 68,
};

const forfeoFitScore = (place: NearbyPlace): number =>
  FORFEO_VERTICAL_PRIORITY[place.primary_type] ?? 35;

const forfeoFitTier = (place: NearbyPlace): ForfeoFitTier => {
  const score = forfeoFitScore(place);
  if (score >= 90) return "A";
  if (score >= 68) return "B";
  return "C";
};

const forfeoFitLabel = (tier: ForfeoFitTier): string =>
  tier === "A"
    ? "Fit Forfeo prioritaire"
    : tier === "B"
      ? "Fit Forfeo pertinent"
      : "Fit Forfeo à qualifier";

const rankedForfyPlaces = (places: NearbyPlace[]): NearbyPlace[] =>
  [...places].sort((left, right) => {
    const scoreDelta =
      forfeoFitScore(right) - forfeoFitScore(left);

    if (scoreDelta !== 0) return scoreDelta;

    return (
      (left.distance_m ?? Number.MAX_SAFE_INTEGER)
      - (right.distance_m ?? Number.MAX_SAFE_INTEGER)
    );
  });

const sha256Hex = async (value: string): Promise<string> => {
  const digest =
    await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(value),
    );

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const nearbyLeadIdempotencyKey = async (
  providerPlaceId: string,
): Promise<string> => {
  const digest = await sha256Hex(providerPlaceId);
  return `nearby-lead:${digest.slice(0, 48)}`;
};

const asText = (value: unknown, fallback = "—"): string =>
  typeof value === "string" && value.trim()
    ? value.trim()
    : fallback;

const formatDistance = (meters: number | null): string => {
  if (meters === null || !Number.isFinite(meters)) return "Distance inconnue";
  if (meters < 1000) return `${Math.max(1, Math.round(meters))} m`;
  return `${(meters / 1000).toFixed(meters < 3000 ? 1 : 0)} km`;
};

const mapsUrl = (place: NearbyPlace): string => {
  const query =
    place.latitude !== null && place.longitude !== null
      ? `${place.latitude},${place.longitude}`
      : place.name;

  const params = new URLSearchParams({
    api: "1",
    query,
  });

  if (place.provider_place_id) {
    params.set("query_place_id", place.provider_place_id);
  }

  return `https://www.google.com/maps/search/?${params.toString()}`;
};

const invokePrpEdge = async (
  functionName: string,
  body: AnyRecord,
): Promise<AnyRecord> => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError) throw sessionError;

  const token = session?.access_token?.trim();
  if (!token) throw new Error("prp_session_required");

  const result = await supabase.functions.invoke(
    functionName,
    {
      body,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!result.error) {
    return (result.data || {}) as AnyRecord;
  }

  let code =
    typeof (result.data as AnyRecord | null)?.error === "string"
      ? String((result.data as AnyRecord).error)
      : "";

  const context = (result.error as any)?.context;
  const response =
    typeof Response !== "undefined" && context instanceof Response
      ? context
      : typeof Response !== "undefined" && context?.response instanceof Response
        ? context.response
        : null;

  if (!code && response) {
    try {
      const payload = await response.clone().json();
      if (typeof payload?.error === "string") {
        code = payload.error;
      }
    } catch {
      // Keep generic safe error.
    }
  }

  throw new Error(code || result.error.message || "prp_edge_failed");
};

export default function PrpNearbyScoutPanel() {
  const [consent, setConsent] = useState(false);
  const [position, setPosition] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [locating, setLocating] = useState(false);
  const [radiusKm, setRadiusKm] =
    usePrpSessionState<number>(
      "nearby.radiusKm",
      10,
      (value) =>
        typeof value === "number"
        && Number.isInteger(value)
        && value >= 1
        && value <= 100,
    );

  const [selectedCategories, setSelectedCategories] =
    usePrpSessionState<NearbyCategoryKey[]>(
      "nearby.selectedCategories",
      [
        "restaurants",
        "spas",
        "hotels",
        "cafes",
      ],
      (value) =>
        Array.isArray(value)
        && value.every(
          (key) =>
            typeof key === "string"
            && CATEGORY_OPTIONS.some(
              (option) =>
                option.key === key,
            ),
        ),
    );
  const [places, setPlaces] = useState<NearbyPlace[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchMeta, setSearchMeta] = useState<AnyRecord | null>(null);
  const [scouting, setScouting] = useState(false);
  const [scoutResult, setScoutResult] = useState<AnyRecord | null>(null);
  const [saveStates, setSaveStates] =
    useState<Record<string, NearbySaveState>>({});

  const scoutOutput =
    scoutResult?.output
    && typeof scoutResult.output === "object"
    && !Array.isArray(scoutResult.output)
      ? scoutResult.output as AnyRecord
      : null;

  const selectedCategoryLabels = useMemo(
    () =>
      CATEGORY_OPTIONS
        .filter((option) => selectedCategories.includes(option.key))
        .map((option) => option.label),
    [selectedCategories],
  );

  const toggleCategory = (key: NearbyCategoryKey) => {
    setSelectedCategories((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key],
    );
    setPlaces([]);
    setScoutResult(null);
  };

  const requestPosition = () => {
    setSearchError("");
    setScoutResult(null);

    if (!consent) {
      setSearchError(
        "Confirme d’abord l’autorisation temporaire de localisation.",
      );
      return;
    }

    if (!("geolocation" in navigator)) {
      setSearchError(
        "La localisation de l’appareil n’est pas disponible dans ce navigateur.",
      );
      return;
    }

    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      (result) => {
        setPosition({
          latitude: result.coords.latitude,
          longitude: result.coords.longitude,
        });
        setPlaces([]);
        setSearchMeta(null);
        setLocating(false);
      },
      (error) => {
        const message =
          error.code === error.PERMISSION_DENIED
            ? "Permission de localisation refusée."
            : error.code === error.TIMEOUT
              ? "La localisation a expiré. Réessaie."
              : "Impossible d’obtenir la position actuelle.";
        setSearchError(message);
        setLocating(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 60000,
      },
    );
  };

  const clearPosition = () => {
    setPosition(null);
    setPlaces([]);
    setSearchMeta(null);
    setScoutResult(null);
    setSearchError("");
    setConsent(false);
  };

  const searchNearby = async () => {
    if (!position) {
      setSearchError("Utilise d’abord ta position actuelle.");
      return;
    }

    if (selectedCategories.length === 0) {
      setSearchError("Choisis au moins une catégorie.");
      return;
    }

    setSearching(true);
    setSearchError("");
    setPlaces([]);
    setSearchMeta(null);
    setScoutResult(null);
    setSaveStates({});

    try {
      const payload = await invokePrpEdge(
        "prp-nearby-search",
        {
          action: "search_nearby",
          center: position,
          radius_m: radiusKm * 1000,
          categories: selectedCategories,
        },
      );

      const rows =
        Array.isArray(payload.results)
          ? payload.results as AnyRecord[]
          : [];

      const normalized: NearbyPlace[] =
        rows
          .map((row) => ({
            provider_place_id: asText(row.provider_place_id, ""),
            name: asText(row.name, "Commerce"),
            address: asText(row.address, "Adresse non fournie"),
            primary_type: asText(row.primary_type, "local_business"),
            primary_type_label: asText(
              row.primary_type_label,
              asText(row.primary_type, "Commerce local"),
            ),
            latitude:
              Number.isFinite(Number(row.latitude))
                ? Number(row.latitude)
                : null,
            longitude:
              Number.isFinite(Number(row.longitude))
                ? Number(row.longitude)
                : null,
            distance_m:
              Number.isFinite(Number(row.distance_m))
                ? Number(row.distance_m)
                : null,
            source: "google_maps_places" as const,
            verification_state: "temporary_unverified" as const,
          }))
          .filter((row) => row.provider_place_id && row.name);

      setPlaces(normalized);
      setSearchMeta(payload);
    } catch (error) {
      const code =
        error instanceof Error
          ? error.message
          : "nearby_search_failed";

      if (code === "position_outside_assigned_region") {
        setSearchError(
          "Ta position actuelle est hors de ta région PRP assignée. La recherche est bloquée pour éviter d’empiéter sur un autre territoire.",
        );
      } else if (code === "prp_region_assignment_required") {
        setSearchError(
          "Aucune région PRP officielle n’est assignée à ce compte. La recherche territoriale est bloquée.",
        );
      } else if (code === "territory_boundary_service_unavailable") {
        setSearchError(
          "La limite officielle de région n’a pas pu être vérifiée. La recherche est bloquée par sécurité.",
        );
      } else if (code === "nearby_provider_not_configured") {
        setSearchError(
          "La source Places serveur n’est pas encore configurée. Aucun commerce n’a été inventé.",
        );
      } else if (code.startsWith("places_provider_http_")) {
        setSearchError(
          `La source de commerces a refusé la recherche (${code}). Réessaie plus tard.`,
        );
      } else {
        setSearchError(
          `Recherche locale indisponible (${code}).`,
        );
      }
    } finally {
      setSearching(false);
    }
  };

  const saveNearbyProspect = async (
    place: NearbyPlace,
  ) => {
    const providerPlaceId =
      place.provider_place_id.trim();

    if (!providerPlaceId) return;

    const current =
      saveStates[providerPlaceId];

    if (
      current?.state === "saving"
      || current?.state === "saved"
      || current?.state === "duplicate"
    ) {
      return;
    }

    setSaveStates((states) => ({
      ...states,
      [providerPlaceId]: {
        state: "saving",
      },
    }));

    try {
      const idempotencyKey =
        await nearbyLeadIdempotencyKey(
          providerPlaceId,
        );

      const payload =
        await invokePrpEdge(
          "prp-workspace-command",
          {
            action:
              "nearby_lead_save",
            idempotency_key:
              idempotencyKey,
            source:
              "google_maps_places",
            provider_place_id:
              providerPlaceId,
            verification_state:
              "temporary_unverified",
            business_name:
              place.name,
            address:
              place.address,
            category:
              place.primary_type_label,
            primary_type:
              place.primary_type,
            source_latitude:
              place.latitude,
            source_longitude:
              place.longitude,
            distance_m:
              place.distance_m,
          },
        );

      const result =
        payload.result
        && typeof payload.result === "object"
        && !Array.isArray(payload.result)
          ? payload.result as AnyRecord
          : {};

      const duplicate =
        payload.duplicate === true
        || result.duplicate === true;

      const leadId =
        asText(
          result.lead_id
          || result.id
          || payload.target_id,
          "",
        );

      setSaveStates((states) => ({
        ...states,
        [providerPlaceId]: {
          state:
            duplicate
              ? "duplicate"
              : "saved",
          lead_id:
            leadId || undefined,
        },
      }));

      if (!duplicate) {
        window.dispatchEvent(
          new CustomEvent(
            "forfeo:prp-workspace-refresh",
            {
              detail: {
                source:
                  "nearby_lead_save",
                lead_id:
                  leadId || null,
              },
            },
          ),
        );
      }
    } catch (error) {
      setSaveStates((states) => ({
        ...states,
        [providerPlaceId]: {
          state: "error",
          error:
            error instanceof Error
              ? error.message
              : "nearby_prospect_save_failed",
        },
      }));
    }
  };

  const runForfyScout = async () => {
    if (places.length === 0) return;

    setScouting(true);
    setScoutResult(null);

    try {
      const candidates =
        rankedForfyPlaces(places)
          .slice(0, 8)
          .map((place) => ({
          provider_place_id: place.provider_place_id,
          business_name: place.name,
          address: place.address,
          category: place.primary_type_label,
          distance_m: place.distance_m,
          source: place.source,
          verification_state: place.verification_state,
          forfeo_fit_tier: forfeoFitTier(place),
          forfeo_fit_score: forfeoFitScore(place),
        }));

      const payload = await invokePrpEdge(
        "prp-forfy-agent-run",
        {
          action: "run_prp_forfy_agent",
          agent_key: "forfy_prp_scout",
          context: {
            business_name:
              `Autour de moi — ${candidates.length} cibles réelles`,
            city: "Zone autour du PRP",
            category: selectedCategoryLabels.join(", "),
            status: "opportunités temporaires non vérifiées",
            notes: JSON.stringify({
              nearby_source: "google_maps_places",
              radius_km: radiusKm,
              candidates,
            }),
            source: "prp_nearby_search",
          },
        },
      );

      setScoutResult(payload);
    } catch (error) {
      setScoutResult({
        ok: false,
        error:
          error instanceof Error
            ? error.message
            : "forfy_scout_failed",
      });
    } finally {
      setScouting(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
      data-lot0f3-prp-nearby-scout="true"
    >
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.28em] text-violet-700">
            0F-4 · Terrain PRP + Pipeline
          </p>
          <h3 className="mt-1 text-xl font-black text-slate-950">
            Autour de moi
          </h3>
          <p className="mt-1 max-w-3xl text-sm font-semibold leading-6 text-slate-600">
            Découvre des commerces réels autour de ta position actuelle.
            La position de l’appareil reste temporaire et n’est jamais enregistrée
            comme coordonnée officielle d’un commerce.
          </p>
        </div>

        <div className="rounded-2xl border border-violet-100 bg-violet-50 px-4 py-3 text-xs font-bold text-violet-900">
          Sauvegarde prospect uniquement après clic humain
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            checked={consent}
            className="mt-1 h-4 w-4"
            onChange={(event) => {
              setConsent(event.target.checked);
              if (!event.target.checked) {
                clearPosition();
              }
            }}
            type="checkbox"
          />
          <span className="text-sm font-bold leading-6 text-slate-700">
            J’autorise l’utilisation temporaire de ma position pour cette recherche.
            Elle n’est ni enregistrée dans le CRM ni utilisée pour valider les
            coordonnées d’un commerce.
          </span>
        </label>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-40"
            disabled={!consent || locating}
            onClick={requestPosition}
            type="button"
          >
            {locating
              ? "Localisation…"
              : position
                ? "Actualiser ma position"
                : "Utiliser ma position"}
          </button>

          {position && (
            <button
              className="rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-black text-slate-700"
              onClick={clearPosition}
              type="button"
            >
              Effacer ma position
            </button>
          )}

          <div className="flex items-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-700">
            {position
              ? "Position disponible pour cette session uniquement"
              : "Aucune position utilisée"}
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-black text-slate-900">
              Rayon de prospection
            </p>
            <p className="mt-1 text-xs font-semibold text-slate-500">
              Curseur continu de 1 à 100 km. Le serveur coupe toujours les résultats
              à la région administrative assignée au PRP.
            </p>
          </div>

          <div className="rounded-2xl bg-violet-700 px-4 py-2 text-lg font-black text-white">
            {radiusKm} km
          </div>
        </div>

        <input
          aria-label="Rayon de prospection en kilomètres"
          className="mt-4 w-full accent-violet-700"
          max={RADIUS_MAX_KM}
          min={RADIUS_MIN_KM}
          onChange={(event) => {
            const next =
              Math.max(
                RADIUS_MIN_KM,
                Math.min(
                  RADIUS_MAX_KM,
                  Math.round(Number(event.target.value)),
                ),
              );

            setRadiusKm(next);
            setPlaces([]);
            setScoutResult(null);
          }}
          step={1}
          type="range"
          value={radiusKm}
        />

        <div className="mt-2 flex justify-between text-[11px] font-black text-slate-400">
          <span>1 km</span>
          <span>25</span>
          <span>50</span>
          <span>75</span>
          <span>100 km</span>
        </div>

        {radiusKm > 50 && (
          <div className="mt-3 rounded-xl border border-violet-100 bg-white px-3 py-2 text-xs font-bold text-violet-900">
            Recherche multi-zone : Google limite chaque cercle Nearby à 50 km.
            Forfeo assemble plusieurs zones côté serveur, puis élimine tout résultat
            hors de ton rayon ou hors de ta région PRP.
          </div>
        )}
      </div>

      <div className="mt-5">
        <p className="text-sm font-black text-slate-900">
          Catégories
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {CATEGORY_OPTIONS.map((option) => {
            const selected = selectedCategories.includes(option.key);

            return (
              <button
                aria-pressed={selected}
                className={`rounded-full border px-4 py-2 text-sm font-black ${
                  selected
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : "border-slate-200 bg-white text-slate-600"
                }`}
                key={option.key}
                onClick={() => toggleCategory(option.key)}
                type="button"
              >
                {selected ? "✓ " : ""}
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          className="rounded-2xl bg-violet-700 px-6 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-40"
          disabled={!position || searching || selectedCategories.length === 0}
          onClick={searchNearby}
          type="button"
        >
          {searching
            ? "Recherche en cours…"
            : `Trouver autour de moi · ${radiusKm} km`}
        </button>

        {places.length > 0 && (
          <button
            className="rounded-2xl bg-slate-950 px-6 py-3 text-sm font-black text-white disabled:opacity-50"
            disabled={scouting}
            onClick={runForfyScout}
            type="button"
          >
            {scouting
              ? "Forfy Scout analyse…"
              : "Prioriser avec Forfy Scout"}
          </button>
        )}
      </div>

      {searchError && (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-800">
          {searchError}
        </div>
      )}

      {searchMeta?.territory_region && (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
            Territoire PRP verrouillé
          </p>
          <p className="mt-1 text-sm font-black text-emerald-950">
            {asText(searchMeta.territory_region)}
          </p>
          <p className="mt-1 text-xs font-semibold text-emerald-800">
            {asText(searchMeta.outside_territory_filtered_count, "0")} résultat(s)
            hors région exclus côté serveur · source limite : Gouvernement du Québec / MRNF.
          </p>
        </div>
      )}

      {places.length > 0 && (
        <div className="mt-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
                Résultats temporaires
              </p>
              <p className="text-lg font-black text-slate-950">
                {places.length} commerce{places.length > 1 ? "s" : ""} trouvé{places.length > 1 ? "s" : ""}
              </p>
            </div>
            <p className="text-xs font-bold text-slate-500">
              Données de découverte : Google Maps
            </p>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {places.map((place) => (
              <article
                className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                key={place.provider_place_id}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-black text-slate-950">
                      {place.name}
                    </p>
                    <p className="mt-1 text-xs font-black uppercase tracking-[0.16em] text-violet-600">
                      {place.primary_type_label}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-black text-slate-600">
                    {formatDistance(place.distance_m)}
                  </span>
                </div>

                <p className="mt-3 text-sm font-semibold leading-5 text-slate-600">
                  {place.address}
                </p>

                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-black text-amber-900">
                  Opportunité temporaire · non vérifiée
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-xl border border-violet-100 bg-violet-50 px-3 py-2 text-xs font-black text-violet-800">
                    {forfeoFitLabel(forfeoFitTier(place))}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700"
                    href={mapsUrl(place)}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Voir sur Google Maps
                  </a>

                  <button
                    className="rounded-xl bg-slate-950 px-3 py-2 text-xs font-black text-white disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={
                      saveStates[place.provider_place_id]?.state === "saving"
                      || saveStates[place.provider_place_id]?.state === "saved"
                      || saveStates[place.provider_place_id]?.state === "duplicate"
                    }
                    onClick={() => saveNearbyProspect(place)}
                    type="button"
                  >
                    {saveStates[place.provider_place_id]?.state === "saving"
                      ? "Sauvegarde…"
                      : saveStates[place.provider_place_id]?.state === "duplicate"
                        ? "✓ Déjà dans vos prospects"
                        : saveStates[place.provider_place_id]?.state === "saved"
                          ? "✓ Prospect sauvegardé"
                          : "Sauvegarder comme prospect"}
                  </button>
                </div>

                {(
                  saveStates[place.provider_place_id]?.state === "saved"
                  || saveStates[place.provider_place_id]?.state === "duplicate"
                ) && (
                  <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-900">
                    {saveStates[place.provider_place_id]?.state === "duplicate"
                      ? "Ce commerce est déjà présent dans votre Pipeline CRM."
                      : "Prospect créé dans le Pipeline CRM. Recharge le dashboard pour voir la ligne dans le pipeline."}
                  </div>
                )}

                {saveStates[place.provider_place_id]?.state === "error" && (
                  <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-800">
                    Sauvegarde impossible ({saveStates[place.provider_place_id]?.error || "erreur"}).
                  </div>
                )}
              </article>
            ))}
          </div>

          <p className="mt-3 text-xs font-semibold leading-5 text-slate-500">
            Ces résultats restent des opportunités externes non vérifiées.
            Un clic humain peut créer un prospect dans le Pipeline CRM, mais les
            coordonnées Google restent uniquement dans la provenance de l’activité
            et ne deviennent jamais des coordonnées officielles Forfeo.
          </p>
        </div>
      )}

      {scoutResult && (
        <div className="mt-6 rounded-2xl border border-violet-200 bg-violet-50 p-4">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-violet-700">
            Forfy Scout · Priorisation des résultats réels
          </p>

          {scoutResult.ok === false ? (
            <p className="mt-3 text-sm font-bold text-rose-700">
              {asText(scoutResult.error, "Forfy Scout indisponible.")}
            </p>
          ) : (
            <>
              <p className="mt-3 text-sm font-semibold leading-6 text-slate-700">
                {asText(scoutOutput?.summary)}
              </p>

              {Array.isArray(scoutOutput?.prospects) && (
                <div className="mt-4 grid gap-2 md:grid-cols-2">
                  {(scoutOutput?.prospects as AnyRecord[]).map((prospect, index) => (
                    <div
                      className="rounded-xl border border-violet-100 bg-white p-3"
                      key={`${index}-${asText(prospect.business_name, "prospect")}`}
                    >
                      <p className="text-sm font-black text-slate-950">
                        {asText(prospect.business_name, "Commerce")}
                      </p>
                      <p className="mt-1 text-xs font-bold text-slate-500">
                        {asText(prospect.category, "Commerce local")}
                        {" · "}
                        {asText(prospect.city, "Zone PRP")}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <p className="mt-3 text-xs font-bold text-violet-900">
                Analyse assistive seulement. Forfy n’a créé aucun prospect,
                validé aucune coordonnée et exécuté aucune action externe.
              </p>
            </>
          )}
        </div>
      )}

      {searchMeta?.provider === "google_maps_places" && places.length === 0 && !searchError && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-bold text-slate-600">
          Aucun commerce correspondant n’a été retourné dans ce rayon.
        </div>
      )}
    </section>
  );
}
